import { Components } from '@/api/generated/api';
import { EQuickEntryKind } from '@/composables/quickEntry/quickEntry.enums';
import { ELastViewedSettingsPage } from '@/composables/useLastViewed';
import { ICustomTab } from '@/core/elements/CustomTabs.vue';
import { EAssessmentVisualizationMode } from '@/interfaces';
import { defineStore } from 'pinia';

export interface ILastViewedState {
  accordionOpenById: Record<string, boolean>;
  clearableAccordionOpenById: Record<string, boolean>;
  lastViewedCompetence: Components.Schemas.CompetenceDto | null;
  lastViewedFaqArticle: Components.Schemas.FaqDto | null;
  lastViewedAssessmentVisualizationMode: EAssessmentVisualizationMode;
  lastViewedReportGroupId: number | null;
  lastViewedReportId: number | null;
  lastViewedSchoolLevelFilter: number;
  lastViewedSchoolSubjectId: number | null;
  lastViewedPerformanceAssessmentId: number | null;
  lastUsedQuickEntryType: EQuickEntryKind | null;
  lastUsedQuickEntryPerformanceAssessmentId: number | null;
  lastViewedSchoolTerm: Components.Schemas.SchoolTermDto | null;
  lastViewedSettingsPage: ELastViewedSettingsPage | null;
  lastViewedStudentId: number | null;
  lastViewedTabPerformanceAssessmentsPageLabel: string | null;
  lastViewedTabSkillsPageLabel: string | null;
  lastViewedTabStatisticsPageLabel: string | null;
  lastViewedTabStudentsPageLabel: string | null;
}

export const useLastViewedStore = defineStore('lastViewed', {
  actions: {
    setAccordionOpenById(id: string, open: boolean, clearable: boolean = false): void {
      this.$patch((state) => {
        if (clearable) {
          state.clearableAccordionOpenById[id] = open;
        } else {
          state.accordionOpenById[id] = open;
        }
      });
    },
    resetOpenAccordions(): void {
      this.$patch((state) => {
        state.clearableAccordionOpenById = {};
      });
    },
    setLastViewedTabStudentsPage(lastViewed: ICustomTab | null): void {
      this.$patch((state) => {
        state.lastViewedTabStudentsPageLabel = lastViewed?.label ?? null;
      });
    },
    setLastViewedTabSkillsPage(lastViewed: ICustomTab | null): void {
      this.$patch((state) => {
        state.lastViewedTabSkillsPageLabel = lastViewed?.label ?? null;
      });
    },
    setLastViewedStudentId(lastViewed: number | null): void {
      this.$patch((state) => {
        state.lastViewedStudentId = lastViewed;
      });
    },
    setLastViewedCompetence(lastViewed: Components.Schemas.CompetenceDto | null): void {
      this.$patch((state) => {
        state.lastViewedCompetence = lastViewed;
      });
    },
    setLastViewedReportId(lastViewed: number | null): void {
      this.$patch((state) => {
        state.lastViewedReportId = lastViewed;
      });
    },
    setLastViewedReportGroupId(lastViewed: number | null): void {
      this.$patch((state) => {
        state.lastViewedReportGroupId = lastViewed;
      });
    },
    setLastViewedPerformanceAssessmentId(lastViewed: number | null): void {
      this.$patch((state) => {
        state.lastViewedPerformanceAssessmentId = lastViewed;
      });
    },
    setLastUsedQuickEntryType(type: EQuickEntryKind, performanceAssessmentId: number | null): void {
      this.$patch((state) => {
        state.lastUsedQuickEntryType = type;
        state.lastUsedQuickEntryPerformanceAssessmentId =
          type === EQuickEntryKind.PerformanceAssessment ? performanceAssessmentId : null;
      });
    },

    setLastViewedSchoolTerm(lastViewed: Components.Schemas.SchoolTermDto | null): void {
      this.$patch((state) => {
        state.lastViewedSchoolTerm = lastViewed;
      });
    },
    setLastViewedSchoolSubjectId(lastViewed: number | null): void {
      this.$patch((state) => {
        state.lastViewedSchoolSubjectId = lastViewed;
      });
    },
    setLastViewedSettingsPage(lastViewed: ELastViewedSettingsPage | null): void {
      this.$patch((state) => {
        state.lastViewedSettingsPage = lastViewed;
      });
    },
    setLastViewedSchoolLevelFilter(lastViewed: number): void {
      this.$patch((state) => {
        state.lastViewedSchoolLevelFilter = lastViewed;
      });
    },
    setLastViewedTabPerformanceAssessmentsPage(lastViewed: ICustomTab | null): void {
      this.$patch((state) => {
        state.lastViewedTabPerformanceAssessmentsPageLabel = lastViewed?.label ?? null;
      });
    },
    setLastViewedFaqArticle(lastViewed: Components.Schemas.FaqDto | null): void {
      this.$patch((state) => {
        state.lastViewedFaqArticle = lastViewed;
      });
    },
    setLastViewedAssessmentVisualizationMode(lastViewed: EAssessmentVisualizationMode): void {
      this.$patch((state) => {
        state.lastViewedAssessmentVisualizationMode = lastViewed;
      });
    },
    setLastViewedTabStatisticsPage(lastViewed: ICustomTab | null): void {
      this.$patch((state) => {
        state.lastViewedTabStatisticsPageLabel = lastViewed?.label ?? null;
      });
    },
  },

  getters: {
    getAccordionOpenById: (state) => {
      return (id: string, clearable: boolean = false, defaultOpen: boolean = false): boolean =>
        clearable
          ? (state.clearableAccordionOpenById[id] ?? defaultOpen)
          : (state.accordionOpenById[id] ?? defaultOpen);
    },
  },

  state: (): ILastViewedState => ({
    accordionOpenById: {},
    clearableAccordionOpenById: {},
    lastViewedCompetence: null,
    lastViewedFaqArticle: null,
    lastViewedAssessmentVisualizationMode: EAssessmentVisualizationMode.Journal,
    lastViewedReportGroupId: null,
    lastViewedReportId: null,
    lastViewedSchoolLevelFilter: 1,
    lastViewedSchoolSubjectId: null,
    lastViewedPerformanceAssessmentId: null,
    lastUsedQuickEntryType: null,
    lastUsedQuickEntryPerformanceAssessmentId: null,
    lastViewedSchoolTerm: null,
    lastViewedSettingsPage: null,
    lastViewedStudentId: null,
    lastViewedTabPerformanceAssessmentsPageLabel: null,
    lastViewedTabSkillsPageLabel: null,
    lastViewedTabStatisticsPageLabel: null,
    lastViewedTabStudentsPageLabel: null,
  }),

  persist: true,
});
